import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { useHaptics } from "@/hooks/use-haptics";
import { useSwipeTabs } from "@/hooks/use-swipe-tabs";
import SwipeAnimatedContent from "@/components/SwipeAnimatedContent";
import TabDots from "@/components/TabDots";
import { DuctSizingCalc, EquivalentDuct, CfmCalc, VelocityNoiseCalc } from "@/components/ductwork/SizingTools";
import { FrictionLossCalc, TotalEffectiveLengthCalc, DuctHeatCalc, DuctLeakageCalc, DuctInsulationCalc } from "@/components/ductwork/PressureTools";
import { DuctWeightCalc, ExpansionCalc, HangerSpacingCalc } from "@/components/ductwork/PropertyTools";
import { maxCfmHeaders, maxCfmRows, fittingEqHeaders, fittingEqRows, cfmRoomHeaders, cfmRoomRows } from "@/components/ductwork/duct-reference-data";
import { SuperheatCalculator, SubcoolingCalculator, LineSizingCalculator, ChargeEstimator, TempPressureLookup } from "@/components/ductwork/RefrigerantTools";
import { ptChartHeaders, ptChartRows, lineSizingHeaders, lineSizingRows } from "@/components/ductwork/refrigerant-data";
import { PsychrometricCalc, CoilSizingCalc, CondensateDrainCalc, DuctAreaCalc } from "@/components/ductwork/DuctExtras";
import TradeFormulas from "@/components/TradeFormulas";

const DUCT_CATEGORIES = [
  {
    id: "sizing", label: "Sizing", color: "bg-primary",
    tools: [
      { value: "sizing", label: "Duct Size" },
      { value: "equivalent", label: "Equiv" },
      { value: "cfm", label: "CFM Calc" },
      { value: "velocity-nc", label: "Velocity" },
      { value: "ductarea", label: "Area" },
      { value: "capacity", label: "Max CFM" },
    ],
  },
  {
    id: "pressure", label: "Pressure", color: "bg-primary",
    tools: [
      { value: "friction", label: "Friction" },
      { value: "tel", label: "TEL" },
      { value: "fittings", label: "Eq. Len" },
      { value: "heat", label: "Heat" },
      { value: "leakage", label: "Leakage" },
      { value: "insulation", label: "R-Value" },
    ],
  },
  {
    id: "properties", label: "Properties", color: "bg-primary",
    tools: [
      { value: "weight", label: "Weight" },
      { value: "expansion", label: "Expand" },
      { value: "hangers", label: "Hangers" },
      { value: "psychro", label: "Psychro" },
      { value: "coil", label: "Coil BTU" },
      { value: "condensate", label: "Condens." },
      { value: "room-cfm", label: "Room CFM" },
      { value: "formulas", label: "Formulas" },
    ],
  },
  {
    id: "hvacr", label: "HVAC/R", color: "bg-copper",
    tools: [
      { value: "superheat", label: "SH" },
      { value: "subcooling", label: "SC" },
      { value: "linesizing", label: "Lines" },
      { value: "charge", label: "Charge" },
      { value: "tplookup", label: "T↔P" },
      { value: "ptchart", label: "P-T Chart" },
      { value: "linetbl", label: "Line Tbl" },
    ],
  },
];

const Ductwork = () => {
  const { lightTap } = useHaptics();
  const [searchParams] = useSearchParams();

  const requestedTool = searchParams.get("tool");
  const findCategoryForTool = (toolValue: string) =>
    DUCT_CATEGORIES.find(c => c.tools.some(t => t.value === toolValue));
  const initialCat = requestedTool ? findCategoryForTool(requestedTool) : null;

  const [category, setCategory] = useState(initialCat?.id ?? "sizing");
  const [activeTool, setActiveTool] = useState(requestedTool ?? "sizing");
  const currentCategory = DUCT_CATEGORIES.find(c => c.id === category)!;

  useEffect(() => {
    if (requestedTool) {
      const cat = findCategoryForTool(requestedTool);
      if (cat) { setCategory(cat.id); setActiveTool(requestedTool); }
    }
  }, [requestedTool]);

  const handleCategoryChange = (catId: string) => {
    lightTap();
    setCategory(catId);
    const cat = DUCT_CATEGORIES.find(c => c.id === catId)!;
    setActiveTool(cat.tools[0].value);
  };

  const handleToolChange = useCallback((tool: string) => { lightTap(); setActiveTool(tool); }, [lightTap]);

  const toolValues = currentCategory.tools.map(t => t.value);
  const handleBoundarySwipe = useCallback((dir: "left" | "right") => {
    const catIdx = DUCT_CATEGORIES.findIndex(c => c.id === category);
    if (dir === "left" && catIdx < DUCT_CATEGORIES.length - 1) {
      const next = DUCT_CATEGORIES[catIdx + 1];
      lightTap(); setCategory(next.id); setActiveTool(next.tools[0].value);
      toast(next.label, { duration: 1200 });
    } else if (dir === "right" && catIdx > 0) {
      const prev = DUCT_CATEGORIES[catIdx - 1];
      lightTap(); setCategory(prev.id); setActiveTool(prev.tools[prev.tools.length - 1].value);
      toast(prev.label, { duration: 1200 });
    }
  }, [category, lightTap]);
  const swipeHandlers = useSwipeTabs({ tabs: toolValues, activeTab: activeTool, onChangeTab: handleToolChange, onBoundarySwipe: handleBoundarySwipe });

  const renderTool = () => {
    switch (activeTool) {
      case "sizing": return <DuctSizingCalc />;
      case "equivalent": return <EquivalentDuct />;
      case "cfm": return <CfmCalc />;
      case "friction": return <FrictionLossCalc />;
      case "tel": return <TotalEffectiveLengthCalc />;
      case "heat": return <DuctHeatCalc />;
      case "leakage": return <DuctLeakageCalc />;
      case "velocity-nc": return <VelocityNoiseCalc />;
      case "weight": return <DuctWeightCalc />;
      case "insulation": return <DuctInsulationCalc />;
      case "expansion": return <ExpansionCalc />;
      case "hangers": return <HangerSpacingCalc />;
      case "psychro": return <PsychrometricCalc />;
      case "coil": return <CoilSizingCalc />;
      case "condensate": return <CondensateDrainCalc />;
      case "ductarea": return <DuctAreaCalc />;
      case "capacity": return <DataTable headers={maxCfmHeaders} rows={maxCfmRows} searchable />;
      case "fittings": return (
        <>
          <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">
            Approximate equivalent lengths for common duct fittings. Use with friction loss calculations for total system pressure drop.
          </p>
          <DataTable headers={fittingEqHeaders} rows={fittingEqRows} searchable />
        </>
      );
      case "room-cfm": return (
        <>
          <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">
            Rule-of-thumb CFM per room type. Verify with Manual J (residential) or ASHRAE (commercial).
          </p>
          <DataTable headers={cfmRoomHeaders} rows={cfmRoomRows} searchable />
        </>
      );
      case "superheat": return <SuperheatCalculator />;
      case "subcooling": return <SubcoolingCalculator />;
      case "linesizing": return <LineSizingCalculator />;
      case "charge": return <ChargeEstimator />;
      case "tplookup": return <TempPressureLookup />;
      case "ptchart": return <div className="-mx-4"><DataTable headers={ptChartHeaders} rows={ptChartRows} searchable searchPlaceholder="Search P-T chart…" /></div>;
      case "linetbl": return <div className="-mx-4"><DataTable headers={lineSizingHeaders} rows={lineSizingRows} searchable searchPlaceholder="Search line sizes…" favoritesConfig={{ type: "duct", category: "❄️ Refrig. Lines", makeId: (row) => `refline-${row[0]}`, makeName: (row) => `${row[0]} ton`, makeDetail: (row) => `Suction ${row[1]} · Liquid ${row[2]} · HG ${row[3]}` }} /></div>;
      case "formulas": return <TradeFormulas trade="ductwork" />;
      default: return null;
    }
  };

  return (
    <div className="pb-24 animate-fade-in">
      <PageHeader title="🌀 Ductwork" subtitle="Duct sizing, airflow, HVAC/R diagnostics & refrigerant tools" />
      <div className="px-4 mt-2">
        <div className="flex gap-1.5 bg-secondary rounded-lg p-1">
          {DUCT_CATEGORIES.map(cat => (
            <button key={cat.id} onClick={() => handleCategoryChange(cat.id)}
              className={`flex-1 py-2 px-2 rounded-md text-xs font-semibold transition-all ${category === cat.id ? `${cat.color} text-primary-foreground shadow-sm` : "text-muted-foreground hover:text-foreground"}`}>
              {cat.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 mt-2 bg-secondary/50 rounded-lg p-1 overflow-x-auto">
          {currentCategory.tools.map(tool => (
            <button key={tool.value} onClick={() => swipeHandlers.changeTab(tool.value)}
              className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-all whitespace-nowrap ${activeTool === tool.value ? "bg-card text-foreground shadow-sm border border-border" : "text-muted-foreground hover:text-foreground"}`}>
              {tool.label}
            </button>
          ))}
        </div>
      </div>
      <div className="px-4 mt-3" {...{onTouchStart: swipeHandlers.onTouchStart, onTouchMove: swipeHandlers.onTouchMove, onTouchEnd: swipeHandlers.onTouchEnd}}>
        <SwipeAnimatedContent activeKey={activeTool} swipeDirection={swipeHandlers.swipeDirection} edgeBounce={swipeHandlers.edgeBounce} onAnimationDone={swipeHandlers.clearDirection} onEdgeBounceDone={swipeHandlers.clearEdgeBounce}>
          {renderTool()}
        </SwipeAnimatedContent>
        <TabDots total={toolValues.length} activeIndex={toolValues.indexOf(activeTool)} onDotClick={(i) => swipeHandlers.changeTab(toolValues[i])} />
      </div>
    </div>
  );
};

export default Ductwork;
